import { seoTags } from './tags'
import type { SeoMeta, SeoSite } from './types'

/**
 * Writes one page's metadata into `document.head`.
 *
 * The DOM half of the pair described in `tags.ts`. It owns a **marked set** — every
 * element it writes carries `data-kkf-seo` — and applying metadata removes that whole set
 * before writing a fresh one. Replacing rather than patching is what makes navigation
 * safe: after three route changes the head holds one canonical, not three, and never the
 * previous page's `og:image` beside this page's `og:title`.
 *
 * `<title>` is the exception. It is not marked and never removed — the element shipped in
 * `index.html` is reused by assigning `document.title`, which is also what every browser
 * tab and every bookmark reads.
 */

const MARK = 'data-kkf-seo'

export function applySeo(site: SeoSite, meta: SeoMeta): void {
  if (typeof document === 'undefined') return
  const head = document.head

  for (const stale of Array.from(head.querySelectorAll(`[${MARK}]`))) stale.remove()

  for (const tag of seoTags(site, meta)) {
    if (tag.kind === 'title') {
      document.title = tag.text
      continue
    }

    if (tag.kind === 'meta') {
      const el = document.createElement('meta')
      if (tag.name !== undefined) el.setAttribute('name', tag.name)
      if (tag.property !== undefined) el.setAttribute('property', tag.property)
      el.setAttribute('content', tag.content)
      el.setAttribute(MARK, '')
      head.appendChild(el)
      continue
    }

    if (tag.kind === 'link') {
      const el = document.createElement('link')
      el.setAttribute('rel', tag.rel)
      el.setAttribute('href', tag.href)
      el.setAttribute(MARK, '')
      head.appendChild(el)
      continue
    }

    const script = document.createElement('script')
    script.setAttribute('type', 'application/ld+json')
    script.setAttribute(MARK, '')
    // `textContent`, not `innerHTML`: the payload is already-escaped JSON and must not be
    // parsed as markup on its way in.
    script.textContent = tag.json
    head.appendChild(script)
  }
}
